import { useCallback } from 'react';

import { useDateFormatter, useTimeFormatter } from '@mint-lib/formatting-hooks';
import { isValid, parseISO } from 'date-fns';

type DateValue = number | string | Date | null | undefined;

const parseDateValue = (date: DateValue): Date | null => {
  if (date == null) {
    return null;
  }

  const parsedDate = typeof date === 'string' ? parseISO(date) : new Date(date);

  return isValid(parsedDate) ? parsedDate : null;
};

export const useFormattedDate = () => {
  const { formatDate } = useDateFormatter();
  const { format: formatTime } = useTimeFormatter();

  const getFormattedDate = useCallback(
    (date: DateValue): string | null => {
      const parsedDate = parseDateValue(date);

      return parsedDate ? formatDate(parsedDate) : null;
    },
    [formatDate],
  );

  const getFormattedDateWithTime = useCallback(
    (date: DateValue, separator = ' - '): string | null => {
      const parsedDate = parseDateValue(date);

      return parsedDate
        ? `${formatDate(parsedDate)}${separator}${formatTime(parsedDate)}`
        : null;
    },
    [formatDate, formatTime],
  );

  return { getFormattedDate, getFormattedDateWithTime };
};
